import { ContractDuration, GenderOrPrenatal, Insurer, Level } from '../../../api';
import {
  PersonState,
  PersonSupplementaryInsuranceState,
  SupplementaryInsuranceTypes,
} from '../../../states/offer/offer.state';
import { assertPresent, isNotNullOrUndefined } from '../../../utils/typeUtils';
import { Item } from './tracking-ecommerce.events';
import {
  mapBasicSupplementaryInsuranceDetailItem,
  mapComplementarySupplementaryInsuranceDetailItem,
  mapHospitalSupplementaryInsuranceDetailItem,
  mapHospitalVivaSupplementaryInsuranceDetailItem,
  mapOutpatientSupplementaryInsuranceDetailItem,
  mapOutpatientVivaSupplementaryInsuranceDetailItem,
  mapSecondarySupplementaryInsuranceDetailItem,
  mapYouthSupplementaryInsuranceDetailItem,
} from './tracking-ecommerce-supplementary-insurance.map';

export function priceIfPresentOrZero(price: number | undefined): number {
  return price ?? 0;
}

export function getCurrentPersonCartValue(person: PersonState): number {
  const basicPrice = priceIfPresentOrZero(person.basicInsurance?.price);
  const supplementaryPrice = Object.values(person.supplementaryInsurances ?? {})
    .filter(isNotNullOrUndefined)
    .reduce((sum, product) => sum + priceIfPresentOrZero(product.price), 0);
  return basicPrice + supplementaryPrice;
}

export function getPersonCartValueAfterChange(
  person: PersonState,
  { previousProductPrice = 0, newProductPrice = 0 }: { previousProductPrice?: number; newProductPrice?: number } = {},
): number {
  return getCurrentPersonCartValue(person) - previousProductPrice + newProductPrice;
}

export function cartValueAfterAdding(person: PersonState, newProductPrice: number, previousProductPrice = 0): number {
  return getPersonCartValueAfterChange(person, { previousProductPrice, newProductPrice });
}

export function cartValueAfterRemoving(person: PersonState, removedProductPrice: number): number {
  return getPersonCartValueAfterChange(person, { previousProductPrice: removedProductPrice });
}

export function mapSupplementaryInsuranceItemFromState(
  product: SupplementaryInsuranceTypes,
  insurances: PersonSupplementaryInsuranceState,
  youthPackage: Level | undefined,
  price: number,
  gender: GenderOrPrenatal,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  switch (product) {
    case 'basic':
      return mapBasicSupplementaryInsuranceDetailItem(
        assertPresent(insurances.basic, 'insurances.basic').offerType,
        gender,
        price,
        totalPersons,
        contractDuration,
        existingCustomer,
        insurer,
      );
    case 'outpatient':
      return mapOutpatientSupplementaryInsuranceDetailItem(
        assertPresent(insurances.outpatient, 'insurances.outpatient').level,
        gender,
        price,
        totalPersons,
        contractDuration,
        existingCustomer,
        insurer,
      );
    case 'outpatientViva':
      return mapOutpatientVivaSupplementaryInsuranceDetailItem(
        assertPresent(insurances.outpatientViva, 'insurances.outpatientViva').level,
        gender,
        price,
        totalPersons,
        contractDuration,
        existingCustomer,
        insurer,
      );
    case 'complementary':
      return mapComplementarySupplementaryInsuranceDetailItem(
        assertPresent(insurances.complementary, 'insurances.complementary').level,
        gender,
        price,
        totalPersons,
        contractDuration,
        existingCustomer,
        insurer,
      );
    case 'complementaryViva':
      return mapComplementarySupplementaryInsuranceDetailItem(
        assertPresent(insurances.complementaryViva, 'insurances.complementaryViva').level,
        gender,
        price,
        totalPersons,
        contractDuration,
        existingCustomer,
        insurer,
      );
    case 'hospital':
      return mapHospitalSupplementaryInsuranceDetailItem(
        assertPresent(insurances.hospital, 'insurances.hospital').offerType,
        gender,
        price,
        totalPersons,
        contractDuration,
        existingCustomer,
        insurer,
      );
    case 'hospitalViva':
      return mapHospitalVivaSupplementaryInsuranceDetailItem(
        assertPresent(insurances.hospitalViva, 'insurances.hospitalViva').offerType,
        gender,
        price,
        totalPersons,
        contractDuration,
        existingCustomer,
        insurer,
      );
    case 'youth':
      return mapYouthSupplementaryInsuranceDetailItem(
        assertPresent(youthPackage, 'youthPackage'),
        gender,
        price,
        totalPersons,
        contractDuration,
        existingCustomer,
        insurer,
      );
    default:
      return mapSecondarySupplementaryInsuranceDetailItem(
        product,
        gender,
        price,
        totalPersons,
        contractDuration,
        existingCustomer,
        insurer,
      );
  }
}
